import { supabase } from '../lib/supabase';
import { Estado, Cidade, CepCache, SystemUsers, ApiError, WebhookLog } from '../types/sistema';

export const sistemaService = {
  async getEstados(): Promise<Estado[]> {
    const { data, error } = await supabase
      .from('estado')
      .select('*')
      .order('nome', { ascending: true });
    if (error) return [];
    return data || [];
  },

  async createEstado(estado: { nome: string; sigla: string; codigo_ibge?: string }): Promise<{ success: boolean; data?: Estado; error?: string }> {
    try {
      const nome = estado.nome.trim();
      const sigla = estado.sigla.trim().toUpperCase();
      if (!nome || !sigla) {
        return { success: false, error: 'Nome e sigla do estado são obrigatórios.' };
      }

      const { data, error } = await supabase
        .from('estado')
        .insert([{
          nome,
          sigla,
          codigo_ibge: estado.codigo_ibge?.trim() || null
        }])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar estado:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (e: any) {
      console.error('Erro inesperado ao criar estado:', e);
      return { success: false, error: e.message || 'Erro inesperado ao criar estado.' };
    }
  },

  async updateEstado(id: number, estado: { nome: string; sigla: string; codigo_ibge?: string }): Promise<{ success: boolean; data?: Estado; error?: string }> {
    try {
      const nome = estado.nome.trim();
      const sigla = estado.sigla.trim().toUpperCase();
      if (!nome || !sigla) {
        return { success: false, error: 'Nome e sigla do estado são obrigatórios.' };
      }

      const { data, error } = await supabase
        .from('estado')
        .update({
          nome,
          sigla,
          codigo_ibge: estado.codigo_ibge?.trim() || null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar estado:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (e: any) {
      console.error('Erro inesperado ao atualizar estado:', e);
      return { success: false, error: e.message || 'Erro inesperado ao atualizar estado.' };
    }
  },

  async deleteEstado(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      // Verifica se há cidades vinculadas
      const { data: cidadesVinculadas } = await supabase
        .from('cidade')
        .select('id')
        .eq('estado_id', id)
        .limit(1);

      if (cidadesVinculadas && cidadesVinculadas.length > 0) {
        return {
          success: false,
          error: 'Não é possível excluir este estado pois existem cidades cadastradas vinculadas a ele.'
        };
      }

      const { error } = await supabase
        .from('estado')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir estado:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (e: any) {
      console.error('Erro inesperado ao excluir estado:', e);
      return { success: false, error: e.message || 'Erro inesperado ao excluir estado.' };
    }
  },

  async getCidades(estadoId?: number): Promise<Cidade[]> {
    let query = supabase.from('cidade').select('*').order('nome', { ascending: true });
    if (estadoId) {
      query = query.eq('estado_id', estadoId);
    }
    const { data, error } = await query;
    if (error) return [];
    return data || [];
  },

  async createCidade(cidade: { nome: string; estado_id: number; codigo_ibge?: string }): Promise<{ success: boolean; data?: Cidade; error?: string }> {
    try {
      const nome = cidade.nome.trim();
      if (!nome || !cidade.estado_id) {
        return { success: false, error: 'Nome e estado da cidade são obrigatórios.' };
      }

      const { data, error } = await supabase
        .from('cidade')
        .insert([{
          nome,
          estado_id: Number(cidade.estado_id),
          codigo_ibge: cidade.codigo_ibge?.trim() || ''
        }])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar cidade:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (e: any) {
      console.error('Erro inesperado ao criar cidade:', e);
      return { success: false, error: e.message || 'Erro inesperado ao criar cidade.' };
    }
  },

  async updateCidade(id: number, cidade: { nome: string; estado_id: number; codigo_ibge?: string }): Promise<{ success: boolean; data?: Cidade; error?: string }> {
    try {
      const nome = cidade.nome.trim();
      if (!nome || !cidade.estado_id) {
        return { success: false, error: 'Nome e estado da cidade são obrigatórios.' };
      }

      const { data, error } = await supabase
        .from('cidade')
        .update({
          nome,
          estado_id: Number(cidade.estado_id),
          codigo_ibge: cidade.codigo_ibge?.trim() || ''
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar cidade:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (e: any) {
      console.error('Erro inesperado ao atualizar cidade:', e);
      return { success: false, error: e.message || 'Erro inesperado ao atualizar cidade.' };
    }
  },

  async deleteCidade(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('cidade')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir cidade:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (e: any) {
      console.error('Erro inesperado ao excluir cidade:', e);
      return { success: false, error: e.message || 'Erro inesperado ao excluir cidade.' };
    }
  },

  async getCepCache(cep: string): Promise<CepCache | null> {
    const { data, error } = await supabase
      .from('cep_cache')
      .select('*')
      .eq('cep', cep)
      .maybeSingle();

    if (error) return null;
    return data;
  },

  async getUsuarios(): Promise<SystemUsers[]> {
    try {
      const { data, error } = await supabase
        .from('system_users')
        .select('*')
        .order('id', { ascending: true });

      if (!error && data && data.length > 0) {
        return data;
      }

      if (data && data.length === 0) {
        return [];
      }

      // Se ocorreu erro de RLS ou indisponibilidade, apenas retorna lista vazia
      return [];
    } catch (err) {
      console.warn('Erro em getUsuarios:', err);
      return [];
    }
  },

  async getApiErrors(limit = 50): Promise<ApiError[]> {
    const { data, error } = await supabase
      .from('api_error')
      .select('*')
      .order('id', { ascending: false })
      .limit(limit);

    if (error) return [];
    return data || [];
  },

  async getWebhookLogs(limit = 50): Promise<WebhookLog[]> {
    const { data, error } = await supabase
      .from('webhook_log')
      .select('*')
      .order('id', { ascending: false })
      .limit(limit);

    if (error) return [];
    return data || [];
  }
};
